import React from 'react';
import { SvgBoxWithStrips } from '../SvgBoxWithStrips';

/**
 * Room configuration constants for consistent diagram rendering
 * All measurements are in SVG coordinate units (pixels)
 */
const ROOM_CONFIG = {
  width: 415,              // Total SVG canvas width
  height: 320,             // Total SVG canvas height
  roomWidth: 247,          // Interior room width
  roomHeight: 250,         // Interior room height
  roomX: 62,               // Room X position (offset from left)
  roomY: 30,               // Room Y position (offset from top)
  wallWidth: 20,           // Thickness of walls
  primaryColor: '#0B75BD', // Primary blue color for text and borders
  groundColor: '#34A56F',  // Green color for ground line
};

/**
 * RoomDiagram - Educational Room Cross-Section Component
 * 
 * Renders a cross-sectional view of a room for heat loss and thermal transfer visualizations.
 * Shows room interior, walls with insulation patterns, outdoor area, and ground.
 * 
 * The diagram provides a foundation for visualizing:
 * - Heat transfer through walls
 * - Temperature differences between indoor and outdoor
 * - Window placement and heat loss
 * - Particle movement and molecular behavior
 * 
 * @component
 * @param {Object} props - Component properties
 * @param {React.ReactNode} [props.children] - Additional SVG elements to overlay on the diagram
 *                                              (e.g., particles, temperature indicators, arrows)
 * 
 * @returns {JSX.Element} SVG diagram of room cross-section with labeled areas
 * 
 * @example
 * // Basic room diagram
 * <RoomDiagram />
 * 
 * @example
 * // Room with window and custom elements
 * <RoomDiagram>
 *   <WindowRect x={40} y={105} />
 *   <BrownianElement X={150} Y={150} minX={62} maxX={309} minY={30} maxY={280} />
 * </RoomDiagram>
 */
export function RoomDiagram({ children }) {
  const { width, height, roomWidth, roomHeight, roomX, roomY, wallWidth, primaryColor, groundColor } = ROOM_CONFIG;

  return (
    <svg width={width} height={height} xmlns="http://www.w3.org/2000/svg">
      {/* Main room rectangle (interior space) */}
      <rect 
        width={roomWidth} 
        height={roomHeight} 
        x={roomX} 
        y={roomY} 
        stroke={primaryColor} 
        strokeWidth={2} 
        fill="transparent" 
      />

      {/* Left wall with diagonal stripe pattern (insulation) */}
      <SvgBoxWithStrips 
        W={wallWidth} 
        H={roomHeight} 
        X={30} 
        Y={20} 
        angle={45} 
        patternId="room-left-wall" 
      />
      
      {/* Right wall with diagonal stripe pattern (insulation) */}
      <SvgBoxWithStrips 
        W={wallWidth} 
        H={roomHeight} 
        X={300} 
        Y={20} 
        angle={45} 
        patternId="room-right-wall" 
      />

      {/* Area labels */}
      <text x={160} y={50} fill={primaryColor} fontSize="16px">Room</text>
      <text x={350} y={50} fill={primaryColor} fontSize="16px">Outdoor</text>

      {/* Ground line */}
      <line x1={0} x2={381} y1={281} y2={281} stroke={groundColor} strokeWidth={5} />
      <text x={160} y={300} fill={primaryColor} fontSize="16px">Earth</text>

      {/* Render any additional custom elements */}
      {children}
    </svg>
  );
}

/**
 * WindowRect - Window Element for Room Diagram
 * 
 * Renders a window rectangle with label for use within RoomDiagram.
 * Represents a point of heat loss/gain in thermal analysis.
 * 
 * @component
 * @param {Object} props - Component properties
 * @param {number} [props.x=40] - X coordinate of window top-left corner
 * @param {number} [props.y=105] - Y coordinate of window top-left corner
 * @param {number} [props.width=20] - Window width
 * @param {number} [props.height=70] - Window height
 * 
 * @returns {JSX.Element} SVG rectangle and label representing a window
 * 
 * @example
 * <RoomDiagram>
 *   <WindowRect x={40} y={105} width={20} height={70} />
 * </RoomDiagram>
 */
export function WindowRect({ x = 40, y = 105, width = 20, height = 70 }) {
  return (
    <>
      {/* Window rectangle (white fill represents glass) */}
      <rect 
        x={x} 
        y={y} 
        width={width} 
        height={height} 
        stroke="black" 
        strokeWidth={1} 
        fill="white" 
      />
      
      {/* Window label positioned to the right of the window */}
      <text x={x + 30} y={y + 15} fill="#0B75BD" fontSize="14px">Window</text>
    </>
  );
}

export default RoomDiagram;
